import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import {
    Users, Inbox, Mail, AlertTriangle, CheckCircle2, Clock, Sparkles,
} from "lucide-react";

import RecentList from "./components/RecentList";

const StatTile = ({ icon: Icon, label, value, hint, accent, testid }) => (
    <div className="bg-white rounded-2xl border border-[#E5DFD3] p-6 flex flex-col" data-testid={testid}>
        <div className="flex items-center justify-between">
            <span className={`h-10 w-10 rounded-xl grid place-items-center ${accent}`}>
                <Icon className="h-5 w-5" />
            </span>
            {hint && <span className="text-[11px] uppercase tracking-wider text-[#6F6B63]">{hint}</span>}
        </div>
        <p className="mt-5 font-[Outfit] text-3xl font-semibold tracking-tight">{value}</p>
        <p className="mt-1 text-[13px] text-[#6F6B63]">{label}</p>
    </div>
);

const STAT_CARDS = (stats) => [
    { icon: Users,    label: "Referrals (total)",  value: stats?.referrals?.total ?? "—", hint: "all-time",   accent: "bg-[#0E2A47] text-white",                                  testid: "stat-referrals-total" },
    { icon: Clock,    label: "Referrals pending",  value: stats?.referrals?.new   ?? "—", hint: "status: new", accent: "bg-[#C8A24A] text-white",                                  testid: "stat-referrals-new" },
    { icon: Inbox,    label: "Messages (total)",   value: stats?.contacts?.total  ?? "—", hint: "all-time",   accent: "bg-[#1A2230] text-white",                                  testid: "stat-messages-total" },
    { icon: Mail,     label: "Messages unread",    value: stats?.contacts?.new    ?? "—", hint: "status: new", accent: "bg-[#7BA37F] text-white",                                  testid: "stat-messages-new" },
    { icon: Sparkles, label: "Chat visitors",      value: stats?.visitors?.total  ?? "—", hint: "known",      accent: "bg-gradient-to-br from-[#C8A24A] to-[#a98835] text-white", testid: "stat-visitors-total" },
];

export default function AdminOverview() {
    const [stats, setStats] = useState(null);
    const [error, setError] = useState("");
    const [recent, setRecent] = useState({ contacts: [], referrals: [] });

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [s, c, r] = await Promise.all([
                    api.get("/admin/stats"),
                    api.get("/contact", { params: { limit: 5 } }),
                    api.get("/referral", { params: { limit: 5 } }),
                ]);
                if (cancelled) return;
                setStats(s.data);
                setRecent({ contacts: c.data, referrals: r.data });
            } catch (e) {
                if (!cancelled) setError(formatApiError(e.response?.data?.detail) || e.message);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    return (
        <div className="space-y-8" data-testid="admin-overview">
            <div>
                <p className="text-[12px] uppercase tracking-[0.2em] text-[#C8A24A] font-medium">
                    Overview
                </p>
                <h1 className="mt-2 font-[Outfit] text-3xl md:text-4xl font-semibold tracking-tight">
                    Welcome back.
                </h1>
            </div>

            {error && (
                <div className="rounded-xl bg-[#fdf6e8] border border-[#C8A24A]/30 text-[#8a6a1f] px-4 py-3 text-[13.5px]">
                    {error}
                </div>
            )}

            {!stats?.email_configured && (
                <div
                    className="rounded-2xl bg-[#EFEBE0] border border-[#E5DFD3] p-5 flex items-start gap-3"
                    data-testid="email-warning"
                >
                    <AlertTriangle className="h-5 w-5 text-[#C8A24A] mt-0.5 shrink-0" />
                    <div className="text-[13.5px] leading-relaxed">
                        <p className="font-medium text-[#1A2230]">
                            Email notifications are not active yet
                        </p>
                        <p className="text-[#6F6B63] mt-1">
                            New submissions are being saved here in real time,
                            but no email alerts will be sent until a Resend API
                            key is configured on the server.
                        </p>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
                {STAT_CARDS(stats).map((t) => (
                    <StatTile key={t.testid} {...t} />
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <RecentList
                    title="Recent referrals"
                    viewAllHref="/admin/referrals"
                    viewAllTestId="overview-view-all-referrals"
                    testid="recent-referrals"
                    items={recent.referrals}
                    emptyMessage="No referrals yet."
                    renderItem={(r) => (
                        <>
                            <div className="min-w-0">
                                <p className="font-medium text-[14.5px] truncate">
                                    {r.youth_name || r.referrer_name}
                                </p>
                                <p className="text-[12.5px] text-[#6F6B63] truncate">
                                    By {r.referrer_name} · {r.agency_name || r.referral_source || "—"}
                                </p>
                            </div>
                            <span className="text-[11.5px] text-[#6F6B63] whitespace-nowrap">
                                {new Date(r.created_at).toLocaleDateString()}
                            </span>
                        </>
                    )}
                />

                <RecentList
                    title="Recent messages"
                    viewAllHref="/admin/messages"
                    viewAllTestId="overview-view-all-messages"
                    testid="recent-messages"
                    items={recent.contacts}
                    emptyMessage="No messages yet."
                    renderItem={(c) => (
                        <>
                            <div className="min-w-0">
                                <p className="font-medium text-[14.5px] truncate">{c.name}</p>
                                <p className="text-[12.5px] text-[#6F6B63] truncate">
                                    {c.subject || c.message?.slice(0, 64)}
                                </p>
                            </div>
                            <span className="text-[11.5px] text-[#6F6B63] whitespace-nowrap">
                                {new Date(c.created_at).toLocaleDateString()}
                            </span>
                        </>
                    )}
                />
            </div>

            {stats?.email_configured && (
                <div
                    className="rounded-xl bg-[#e9efe5] border border-[#0E2A47]/20 px-4 py-3 text-[13.5px] text-[#0E2A47] inline-flex items-center gap-2"
                    data-testid="email-active"
                >
                    <CheckCircle2 className="h-4 w-4" /> Email alerts are active.
                </div>
            )}
        </div>
    );
}
